import { PreviewContact, PreviewHousehold } from "@/schema"
import { ButtonBadge } from "@ui/button-badge"
import { Badge } from "@ui/shad"
import { ExternalLink, Tag, User, WandSparkles } from "lucide-react"

import { usePeekViewData } from "@/hooks/api/peek-views/queries"
import { Link } from "@/components/book-link"
import { PhoneBadgeWithRingout } from "@/components/tables/phone-badge-with-ringout"

import PeekViewSkeleton from "./skeleton"

export default function PersonPeekView({ personId, href }: { personId: string; href: string | null }) {
  const { data: person, isLoading } = usePeekViewData(personId, "Person") as {
    data: PreviewHousehold | PreviewContact
    isLoading: boolean
  }

  if (isLoading) return <PeekViewSkeleton />

  const isHousehold = "name" in person && "type" in person
  const isContact = "person" in person
  const personName = isHousehold ? person.name : isContact ? person.person?.full_name || "Unnamed contact" : "Unknown"

  return (
    <div className="flex w-xs flex-col items-stretch gap-2 xl:w-sm">
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-baseline">
          <p className="truncate font-medium">{personName}</p>
          {href && (
            <Link
              href={href}
              className="text-muted-foreground hover:text-link ml-2 transition-colors"
              onClick={(e) => e.stopPropagation()}
              aria-label="Open in new page"
            >
              <ExternalLink className="size-3.5" strokeWidth={1.5} />
            </Link>
          )}
        </div>
        <Badge variant="outline" className="flex-none">
          {isHousehold ? person.type : "Contact"}
        </Badge>
      </div>
      <div className="flex items-center gap-2">
        <User className="text-icon size-4 flex-none" />
        {person.email && (
          <ButtonBadge text={person.email || ""} copyText={person.email || ""} className="max-w-1/2 truncate" />
        )}
        {person.phone_number && (
          <PhoneBadgeWithRingout
            value={person.phone_number}
            extension={person.phone_number_extension}
            name={personName}
          />
        )}
      </div>
      {person.tags && person.tags.length > 0 && (
        <div className="flex items-center gap-2 truncate">
          <Tag className="text-icon size-4 flex-none" />
          {person.tags.slice(0, 3).map((tag) => (
            <Badge key={tag.id} variant="secondary" className="flex-none">
              {tag.name}
            </Badge>
          ))}
          {person.tags.length > 3 && (
            <Badge variant="secondary" className="flex-none">
              +{person.tags.length - 3}
            </Badge>
          )}
        </div>
      )}
      {isHousehold && person.ai_summary && (
        <div className="flex items-start gap-2">
          <WandSparkles className="text-icon mt-1 size-4 flex-none" />
          <p className="w-full text-sm">{person.ai_summary}</p>
        </div>
      )}
    </div>
  )
}
